import { collectionItemForRecommendation, orderLayeringRecommendations } from './layeringPicker.js'

// collection_display projects fragrance.fragrances ownership rows, not catalog
// membership. Read every page: a truncated collection must not imply unowned.
export async function fetchPairingCollection(client, userId) {
  if (!userId) return []
  const rows = []
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await client.from('collection_display').select('*')
      .eq('user_id', userId).order('id').range(offset, offset + 999)
    if (error) throw error
    rows.push(...(data || []))
    if (!data || data.length < 1000) return rows
  }
}

export function isMissingOwnedLayeringRpc(error) {
  return error?.code === 'PGRST202' || error?.code === '42883'
}

export function partitionOwnedPairings(candidates, collection, anchorId = '', limit = 5) {
  const active = collection.filter(row => !row.deleted_at && !row.given_away && !row.finished && !row.finished_at)
  const identities = new Set(active.map(row => row.catalog_id || `${row.house}::${row.name}::${row.concentration}`))
  if (identities.size < 2) return { owned: [], unowned: [], needsAnotherBottle: true }
  const owned = []
  const unowned = []
  const anchor = collection.find(row => row.id === anchorId)
  for (const candidate of orderLayeringRecommendations(candidates)) {
    if (!candidate || (anchor?.catalog_id && candidate.catalog_id === anchor.catalog_id)) continue
    // A verified catalog ID must never fall back to a similarly named edition.
    const matches = candidate.catalog_id
      ? collection.filter(row => row.catalog_id === candidate.catalog_id)
      : collection
    const item = collectionItemForRecommendation(candidate, matches, anchorId)
    if (item) owned.push(candidate)
    else {
      // Archived/finished owned bottles are not catalog discovery suggestions.
      const belongs = candidate.catalog_id && collection.some(row => row.catalog_id === candidate.catalog_id)
      if (!belongs) unowned.push(candidate)
    }
  }
  return { owned: owned.slice(0, limit), unowned: unowned.slice(0, limit), needsAnotherBottle: false }
}

export async function fetchOwnedLayeringPage(client, {
  userId,
  targetCatalogId,
  pageSize = 20,
  offsetCount = 0,
  collection = [],
} = {}) {
  const own = collection.filter(row => row.user_id === userId)
  const anchorId = own.find(row => row.catalog_id === targetCatalogId && !row.deleted_at && !row.finished_at && !row.finished && !row.given_away)?.id
  const result = await client.rpc('get_owned_layering_recommendations_v1', {
    target_catalog_id: targetCatalogId,
    limit_count: pageSize,
    offset_count: offsetCount,
  })
  if (!result.error) {
    const candidates = result.data || []
    return {
      rows: partitionOwnedPairings(candidates, own, anchorId, pageSize).owned,
      hasMore: candidates.length === pageSize,
      offset: offsetCount + candidates.length,
    }
  }
  if (isMissingOwnedLayeringRpc(result.error)) {
    // During staggered rollout, preserve owned-first defaults via the legacy
    // bounded read. This still omits owned matches below the first 1,000
    // catalog candidates, but it avoids surfacing unowned defaults.
    const fallback = await client.rpc('get_layering_recommendations', {
      target_catalog_id: targetCatalogId,
      user_id: userId,
      limit_count: 1000,
    })
    if (fallback.error) throw fallback.error
    const owned = partitionOwnedPairings(fallback.data || [], own, anchorId, 1000).owned
    const rows = owned.slice(offsetCount, offsetCount + pageSize)
    return {
      rows,
      hasMore: offsetCount + rows.length < owned.length,
      offset: offsetCount + rows.length,
    }
  }
  throw result.error
}
